import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { Footer } from '@/components';
import defaultSettings from '../config/defaultSettings';
import { currentUser as queryCurrentUser } from './services/ant-design-pro/api';
import '@ant-design/v5-patch-for-react-19';
import {
  AndroidOutlined,
  AppstoreOutlined,
  BookOutlined,
  BranchesOutlined,
  BuildOutlined,
  CodepenOutlined,
  ContactsOutlined,
  ContainerOutlined,
  CopyOutlined,
  CrownOutlined,
  DashboardOutlined,
  DesktopOutlined,
  DollarOutlined,
  DribbbleOutlined,
  FileDoneOutlined,
  FolderOutlined,
  GlobalOutlined,
  GroupOutlined,
  InboxOutlined,
  MessageOutlined,
  ProfileOutlined,
  QrcodeOutlined,
  RiseOutlined,
  RobotOutlined,
  SafetyOutlined,
  SettingOutlined,
  ShoppingOutlined,
  SmileOutlined,
  SolutionOutlined,
  StarOutlined,
  TableOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  WarningOutlined,
  WechatOutlined,
  YoutubeOutlined,
} from '@ant-design/icons';
import RightContent from '@/components/RightContent';
import oauth2 from '@/utils/oauth2';
import utilInit from './utils/init';
import {
  authHeaderInterceptor,
  errorHandler,
  responseInterceptors,
} from './utils/request';

const iconMap: Record<string, React.ReactNode> = {
  smile: <SmileOutlined style={{ color: '#10b981' }} />,
  dashboard: <DashboardOutlined style={{ color: '#3b82f6' }} />,
  rise: <RiseOutlined style={{ color: '#ef4444' }} />,
  wechat: <WechatOutlined style={{ color: '#22c55e' }} />,
  qrcode: <QrcodeOutlined style={{ color: '#06b6d4' }} />,
  contacts: <ContactsOutlined style={{ color: '#f59e0b' }} />,
  group: <GroupOutlined style={{ color: '#8b5cf6' }} />,
  message: <MessageOutlined style={{ color: '#3b82f6' }} />,
  dribbble: <DribbbleOutlined style={{ color: '#ec4899' }} />,
  robot: <RobotOutlined style={{ color: '#8b5cf6' }} />,
  copy: <CopyOutlined style={{ color: '#64748b' }} />,
  book: <BookOutlined style={{ color: '#b45309' }} />,
  warning: <WarningOutlined style={{ color: '#f97316' }} />,
  youtube: <YoutubeOutlined style={{ color: '#ef4444' }} />,
  folder: <FolderOutlined style={{ color: '#eab308' }} />,
  codepen: <CodepenOutlined style={{ color: '#14b8a6' }} />,
  TableOutlined: <TableOutlined style={{ color: '#f59e0b' }} />,
  branches: <BranchesOutlined style={{ color: '#3b82f6' }} />,
  build: <BuildOutlined style={{ color: '#6366f1' }} />,
  solution: <SolutionOutlined style={{ color: '#10b981' }} />,
  container: <ContainerOutlined style={{ color: '#06b6d4' }} />,
  team: <TeamOutlined style={{ color: '#8b5cf6' }} />,
  dollar: <DollarOutlined style={{ color: '#10b981' }} />,
  fileDone: <FileDoneOutlined style={{ color: '#3b82f6' }} />,
  shopping: <ShoppingOutlined style={{ color: '#ec4899' }} />,
  profile: <ProfileOutlined style={{ color: '#6366f1' }} />,
  android: <AndroidOutlined style={{ color: '#10b981' }} />,
  UsergroupAddOutlined: <UsergroupAddOutlined style={{ color: '#f59e0b' }} />,
  RobotOutlined: <RobotOutlined style={{ color: '#8b5cf6' }} />,
  desktop: <DesktopOutlined style={{ color: '#3b82f6' }} />,
  setting: <SettingOutlined style={{ color: '#64748b' }} />,
  safety: <SafetyOutlined style={{ color: '#10b981' }} />,
  global: <GlobalOutlined style={{ color: '#3b82f6' }} />,
  crown: <CrownOutlined style={{ color: '#eab308' }} />,
  inbox: <InboxOutlined style={{ color: '#0ea5e9' }} />,
  star: <StarOutlined style={{ color: '#eab308' }} />,
};

const pathToIconMap: Record<string, React.ReactNode> = {
  '/dashboard': <DashboardOutlined style={{ color: '#3b82f6' }} />,
  '/dashboard/welcome': <SmileOutlined style={{ color: '#10b981' }} />,
  '/dashboard/analysis': <RiseOutlined style={{ color: '#ef4444' }} />,
  '/qywx': <WechatOutlined style={{ color: '#22c55e' }} />,
  '/qywx/qrcode': <QrcodeOutlined style={{ color: '#06b6d4' }} />,
  '/qywx/welcomemessages': <SmileOutlined style={{ color: '#10b981' }} />,
  '/qywx/external-contact': <ContactsOutlined style={{ color: '#f59e0b' }} />,
  '/qywx/group-chat': <GroupOutlined style={{ color: '#8b5cf6' }} />,
  '/qywx/marketing': <MessageOutlined style={{ color: '#3b82f6' }} />,
  '/qywx/moments': <DribbbleOutlined style={{ color: '#ec4899' }} />,
  '/qywx/media': <CopyOutlined style={{ color: '#64748b' }} />,
  '/qywx/quick-reply': <BookOutlined style={{ color: '#b45309' }} />,
  '/qywx/risk': <WarningOutlined style={{ color: '#f97316' }} />,
  '/qywx/chatdata': <WechatOutlined style={{ color: '#22c55e' }} />,
  '/qywx/auto-task': <RobotOutlined style={{ color: '#8b5cf6' }} />,
  '/qywx/living/room': <YoutubeOutlined style={{ color: '#ef4444' }} />,
  '/ai': <RobotOutlined style={{ color: '#8b5cf6' }} />,
  '/ai/annotations': <FolderOutlined style={{ color: '#eab308' }} />,
  '/ai/model': <CodepenOutlined style={{ color: '#14b8a6' }} />,
  '/crm': <TableOutlined style={{ color: '#f59e0b' }} />,
  '/crm/lead': <BranchesOutlined style={{ color: '#3b82f6' }} />,
  '/crm/lead-commons': <BuildOutlined style={{ color: '#6366f1' }} />,
  '/crm/customer': <SolutionOutlined style={{ color: '#10b981' }} />,
  '/crm/customer-commons': <ContainerOutlined style={{ color: '#06b6d4' }} />,
  '/crm/contacts': <TeamOutlined style={{ color: '#8b5cf6' }} />,
  '/crm/opportunity': <DollarOutlined style={{ color: '#10b981' }} />,
  '/crm/contract': <FileDoneOutlined style={{ color: '#3b82f6' }} />,
  '/crm/product': <ShoppingOutlined style={{ color: '#ec4899' }} />,
  '/crm/activity': <ProfileOutlined style={{ color: '#6366f1' }} />,
  '/android': <AndroidOutlined style={{ color: '#10b981' }} />,
  '/android/wework/account': <SmileOutlined style={{ color: '#10b981' }} />,
  '/android/wework/friend': <ContactsOutlined style={{ color: '#f59e0b' }} />,
  '/android/wework/group': <TeamOutlined style={{ color: '#8b5cf6' }} />,
  '/android/wework/stranger': (
    <UsergroupAddOutlined style={{ color: '#f59e0b' }} />
  ),
  '/android/wework/ai-agent': <RobotOutlined style={{ color: '#8b5cf6' }} />,
  '/xchat': <MessageOutlined style={{ color: '#3b82f6' }} />,
  '/operation': <AppstoreOutlined style={{ color: '#ec4899' }} />,
  '/operation/device': <DesktopOutlined style={{ color: '#3b82f6' }} />,
  '/admin': <SettingOutlined style={{ color: '#64748b' }} />,
  '/admin/system/user': <UsergroupAddOutlined style={{ color: '#f59e0b' }} />,
  '/admin/system/role': <SafetyOutlined style={{ color: '#10b981' }} />,
  '/admin/system/company': <GlobalOutlined style={{ color: '#3b82f6' }} />,
  '/admin/setting/dict': <InboxOutlined style={{ color: '#0ea5e9' }} />,
  '/admin/setting/field': <StarOutlined style={{ color: '#eab308' }} />,
};

const _isDev = process.env.NODE_ENV === 'development';
const loginPath = PLATFORM === 'h5' ? '/mobile/login' : '/login';

utilInit();

import { unstableSetRender } from 'antd-mobile';
import { createRoot } from 'react-dom/client';

/**
 * ==================================================================
 *  antd-mobile for React 19 兼容性配置
 *  这部分代码必须在应用的最开始执行，以确保 antd-mobile 的
 *  Toast, Modal 等指令式 API 能够正确渲染。
 * ==================================================================
 */
console.log('[antd-pro] Setting up antd-mobile for React 19 compatibility...');

unstableSetRender((node, container) => {
  // 使用 React 18+ 的 createRoot API
  // container._reactRoot ||= ... 是一种优化，确保同一个容器不会重复创建 root
  container._reactRoot ||= createRoot(container);
  const root = container._reactRoot;

  // 渲染 React 节点
  root.render(node);

  // 返回一个卸载函数，antd-mobile 会在组件关闭时调用它
  return async () => {
    // 使用微任务延迟卸载，可以避免一些 React 内部的警告
    await new Promise((resolve) => setTimeout(resolve, 0));
    root.unmount();
  };
});

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(`${loginPath}?redirect=${location.pathname}`);
    }
    return undefined;
  };

  // 根据系统主题决定 navTheme
  const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const navTheme = darkModeMediaQuery.matches ? 'realDark' : 'light';

  // 如果不是登录页面，执行
  const { location } = history;
  if (!location.pathname.includes(loginPath)) {
    await oauth2();

    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: {
        ...(defaultSettings as Partial<LayoutSettings>),
        navTheme,
        title: currentUser?.company || 'SCRM',
      },
    };
  }
  return {
    fetchUserInfo,
    settings: {
      ...(defaultSettings as Partial<LayoutSettings>),
      navTheme,
    },
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 监听系统主题变化
  React.useEffect(() => {
    const darkModeMediaQuery = window.matchMedia(
      '(prefers-color-scheme: dark)',
    );
    const handleThemeChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'realDark' : 'light';
      setInitialState?.((prev: any) => ({
        ...prev,
        settings: {
          ...prev?.settings,
          navTheme: newTheme,
        },
      }));
    };

    darkModeMediaQuery.addEventListener('change', handleThemeChange);
    return () =>
      darkModeMediaQuery.removeEventListener('change', handleThemeChange);
  }, [setInitialState]);

  return {
    postMenuData: (menuData) => {
      const mapMenuData = (items: any[]): any[] => {
        return items.map((item) => {
          const newItem = { ...item };
          const path = newItem.path || '';
          if (path && pathToIconMap[path]) {
            newItem.icon = pathToIconMap[path];
          } else {
            const iconKey =
              typeof newItem.icon === 'string' ? newItem.icon : '';
            if (iconKey) {
              if (iconMap[iconKey]) {
                newItem.icon = iconMap[iconKey];
              } else if (iconMap[iconKey.toLowerCase()]) {
                newItem.icon = iconMap[iconKey.toLowerCase()];
              }
            }
          }
          if (newItem.children) {
            newItem.children = mapMenuData(newItem.children);
          }
          return newItem;
        });
      };
      return menuData ? mapMenuData(menuData) : [];
    },
    // actionsRender: () => [
    //   <Question key="doc" />,
    //   <SelectLang key="SelectLang" />,
    // ],
    // avatarProps: {
    //   src: initialState?.currentUser?.avatar,
    //   title: <AvatarName />,
    //   render: (_, avatarChildren) => (
    //     <>{avatarChildren}</>
    //   ),
    // },
    rightContentRender: () => <RightContent />,
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      // const { location } = history;
      // 如果没有登录，重定向到 login
      // if (!initialState?.currentUser && location.pathname !== loginPath) {
      //   history.push(loginPath);
      // }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    pure: PLATFORM === 'h5',
    links: [],
    siderWidth: 206,
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      console.log('children', children);
      return <>{children}</>;
    },
    ...initialState?.settings,
    // title: initialState?.currentUser?.company || 'SCRM'
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  errorHandler,
  requestInterceptors: [authHeaderInterceptor],
  responseInterceptors: [responseInterceptors],
};
